import { useEffect, useRef, useCallback } from 'react';
import type { Chart } from 'klinecharts';
import { api } from '@/services/api';
import { useChartStore } from '@/store/chartStore';
import type { ForeignHoldingChartPoint } from '@/indicators/foreignHolding';

type ChartV9 = Chart & {
  overrideIndicator: (o: { name: string; calcParams?: unknown[] }, paneId?: string) => void;
};

const INDICATOR_NAME = 'FOREIGN_HOLDING';
// Foreign-holding snapshots are meant to land daily; a gap wider than this
// (calendar days) means the underlying report wasn't published for one or
// more days — the delta across it isn't a meaningful single-day change, so
// it's nulled out rather than shown as if it were.
const GAP_DAYS_THRESHOLD = 4;

declare global {
  interface Window {
    __foreign_holding_data?: {
      symbol: string;
      points: ForeignHoldingChartPoint[];
    };
  }
}

export function useForeignHolding(symbol: string) {
  const currentSymbolRef = useRef('');
  const cancelledRef = useRef(false);

  const fetchAndBuild = useCallback(async (sym: string) => {
    currentSymbolRef.current = sym;
    cancelledRef.current = false;
    try {
      const holdingRes = await api.foreignHolding(sym);
      if (cancelledRef.current || currentSymbolRef.current !== sym) return;

      const rows = holdingRes.series
        .map((r) => ({ ...r, time: Math.floor(Date.parse(`${r.date}T00:00:00Z`) / 1000) }))
        .sort((a, b) => a.time - b.time);

      const points: ForeignHoldingChartPoint[] = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const prev = i > 0 ? rows[i - 1] : null;
        const gapDays = prev ? (row.time - prev.time) / 86400 : null;
        const gapBefore = gapDays != null && gapDays > GAP_DAYS_THRESHOLD;

        let delta: number | null = null;
        let deltaPct: number | null = null;
        if (prev && !gapBefore) {
          delta = row.foreignHolding - prev.foreignHolding;
          deltaPct = prev.foreignHolding !== 0 ? (delta / prev.foreignHolding) * 100 : null;
        }

        points.push({
          time: row.time,
          date: row.date,
          pct: row.pct,
          foreignHolding: row.foreignHolding,
          delta,
          deltaPct,
        });
      }

      window.__foreign_holding_data = { symbol: sym, points };

      const { chart: liveChart, indicatorPaneMap: livePaneMap } = useChartStore.getState();
      const paneId = livePaneMap.get(INDICATOR_NAME);
      if (liveChart && paneId) {
        try {
          // `calc()` doesn't actually read calcParams (config lives in the
          // store), but passing a value that changes on every call is what
          // reliably makes klinecharts re-run calc() rather than just
          // repaint with the indicator's previous (pre-fetch, empty)
          // result — an empty `styles: {}` override forces a redraw for
          // custom draw()-based indicators (which re-read external data
          // directly in draw()), but this is a plain figures indicator
          // whose values come from calc()'s cached output, so a redraw
          // alone was leaving the pane empty until something else (e.g. a
          // symbol change) forced a genuine recalc.
          (liveChart as unknown as ChartV9).overrideIndicator({ name: INDICATOR_NAME, calcParams: [Date.now()] }, paneId);
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.error('[useForeignHolding] Failed:', err);
      window.__foreign_holding_data = { symbol: sym, points: [] };
    }
  }, []);

  const enabled = useChartStore((s) => s.indicatorPaneMap.has(INDICATOR_NAME));

  useEffect(() => {
    if (!enabled || !symbol) {
      window.__foreign_holding_data = { symbol: symbol ?? '', points: [] };
      return;
    }
    fetchAndBuild(symbol);
    return () => { cancelledRef.current = true; };
  }, [symbol, enabled, fetchAndBuild]);
}
